import type { ProgramActivity, ProgramActivityStatus } from './types';

const ENDED_PHASES: Array<ProgramActivity['phase']> = ['awarded', 'completed'];
const ACTIVE_PHASES: Array<ProgramActivity['phase']> = ['in_progress', 'in_submission', 'in_evaluation'];

const parseTimestamp = (value?: string) => {
  if (!value) return undefined;

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? undefined : timestamp;
};

const formatDateTime = (value?: string) => {
  const timestamp = parseTimestamp(value);
  if (typeof timestamp !== 'number') return '';

  const date = new Date(timestamp);
  const pad = (unit: number) => String(unit).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
};

export const getProgramActivityTimeRange = (activity: Pick<ProgramActivity, 'start_time' | 'end_time'>) =>
  [formatDateTime(activity.start_time), formatDateTime(activity.end_time)].filter(Boolean).join(' - ');

export const getProgramActivityStatus = (
  activity: Pick<ProgramActivity, 'phase' | 'start_time' | 'end_time'>,
  now = Date.now(),
): ProgramActivityStatus => {
  const endTime = parseTimestamp(activity.end_time);
  const ended = ENDED_PHASES.includes(activity.phase) || (typeof endTime === 'number' && endTime <= now);
  if (ended) return 'ended';
  if (activity.phase === 'not_started') return 'not_started';
  if (ACTIVE_PHASES.includes(activity.phase)) return 'in_progress';

  const startTime = parseTimestamp(activity.start_time);
  if (typeof startTime === 'number' && typeof endTime === 'number') {
    return startTime <= now && now < endTime ? 'in_progress' : 'ended';
  }
  if (typeof startTime === 'number') return startTime <= now ? 'in_progress' : 'ended';
  if (typeof endTime === 'number') return now < endTime ? 'in_progress' : 'ended';

  return 'ended';
};

export const isValidProgramWorkUrl = (value: string) => {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};
